// A folha de lançar UM sinal — abre do toque no cartão do monitor.
//
// Tocou, digitou, gravou, voltou. A hora vem como "agora" e só se mexe nela
// quando a medida foi antes (a enfermeira mediu às 19h e o valor chegou às
// 20h30): errar a hora já aconteceu, então ela fica à vista, com um toque para
// trocar. Quem só acompanha vê o último valor e o caminho para as 24 h.
import { useEffect, useRef, useState, type FormEvent, type RefObject } from 'react';
import { agoraParaCampo, doCampoParaISO, formatarHora, type LeituraDTO, type TipoLeitura } from '@medicov1/shared';
import { chamar } from '../api';
import { montarLancamento, type CamposDoSinal } from './lancamentoDeSinal';

export interface EixoDoSinal { tipo: TipoLeitura; rotulo: string; unidade: string; cor: string }

interface Props {
  eixo: EixoDoSinal;
  pacienteId: string | null;
  ultimo?: LeituraDTO;
  ultimaDiastolica?: LeituraDTO;
  registra: boolean;
  aoGravar: (resumo: string) => void;
  aoVer24h: () => void;
  aoFechar: () => void;
}

export function LancarSinal({ eixo, pacienteId, ultimo, ultimaDiastolica, registra, aoGravar, aoVer24h, aoFechar }: Props) {
  const ehPa = eixo.tipo === 'pa_sistolica';
  const [campos, setCampos] = useState<CamposDoSinal>({});
  const [quando, setQuando] = useState(agoraParaCampo());
  const [mudarHora, setMudarHora] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const primeiro = useRef<HTMLInputElement>(null);

  // O teclado numérico já abre: o toque no cartão foi para digitar.
  useEffect(() => { if (registra) primeiro.current?.focus(); }, [registra]);

  const valorUltimo = ultimo
    ? `${Number(ultimo.valor).toLocaleString('pt-BR')}${ehPa && ultimaDiastolica ? `/${Number(ultimaDiastolica.valor).toLocaleString('pt-BR')}` : ''} ${eixo.unidade} · ${formatarHora(ultimo.medidoEm)}`
    : 'sem leitura registrada';

  async function gravar(e: FormEvent) {
    e.preventDefault();
    if (!pacienteId) return;
    const l = montarLancamento(eixo.tipo, campos, eixo.rotulo, `manual-${Date.now()}`);
    if (!l.ok) { setErro(l.erro); return; }
    setErro('');
    setEnviando(true);
    try {
      const medidoEm = doCampoParaISO(quando);
      for (const item of l.leituras) {
        await chamar('/leituras', { metodo: 'POST', corpo: { pacienteId, origem: 'manual', medidoEm, ...item } });
      }
      const valor = ehPa ? `${campos.sistolica}/${campos.diastolica}` : campos.valor;
      aoGravar(`${eixo.rotulo} ${valor} ${eixo.unidade} gravado`);
    } catch (x) {
      setErro(x instanceof Error ? x.message : 'Não consegui gravar.');
    } finally {
      setEnviando(false);
    }
  }

  const campo = (chave: keyof CamposDoSinal, rotulo: string, ref?: RefObject<HTMLInputElement | null>) => (
    <label className="lancar__campo">
      <span>{rotulo}</span>
      <input ref={ref} type="text" inputMode={eixo.tipo === 'temperatura' ? 'decimal' : 'numeric'}
        autoComplete="off" enterKeyHint={chave === 'sistolica' ? 'next' : 'done'}
        value={campos[chave] ?? ''} placeholder="—"
        onChange={(ev) => setCampos((c) => ({ ...c, [chave]: ev.target.value }))} />
      <small>{eixo.unidade}</small>
    </label>
  );

  return (
    <div className="folha" onClick={aoFechar} role="presentation">
      <form className="folha__caixa lancar" onClick={(e) => e.stopPropagation()} onSubmit={gravar}
        style={{ ['--cor' as string]: eixo.cor }}>
        <div className="folha__topo">
          <b>{eixo.rotulo}</b>
          <small>último: {valorUltimo}</small>
        </div>

        {registra ? (
          <>
            <div className={`lancar__campos${ehPa ? ' lancar__campos--pa' : ''}`}>
              {ehPa ? (
                <>
                  {campo('sistolica', 'Máxima', primeiro)}
                  {campo('diastolica', 'Mínima')}
                </>
              ) : campo('valor', 'Valor', primeiro)}
            </div>

            <div className="lancar__hora">
              {mudarHora ? (
                <label>
                  <span>Hora da medida</span>
                  <input type="datetime-local" value={quando} onChange={(ev) => setQuando(ev.target.value)} required />
                </label>
              ) : (
                <>
                  <span>Medido <b>agora</b>, {quando.slice(11, 16)}</span>
                  <button type="button" onClick={() => setMudarHora(true)}>foi em outra hora</button>
                </>
              )}
            </div>

            {erro && <p className="aviso-erro">{erro}</p>}

            <div className="lancar__botoes">
              <button type="submit" className="lancar__gravar" disabled={enviando}>{enviando ? 'Gravando…' : 'Gravar'}</button>
              <button type="button" className="lancar__cancelar" onClick={aoFechar}>Cancelar</button>
            </div>
          </>
        ) : (
          <p className="lancar__so-ler">Só quem registra lança valores. Você pode ver as últimas 24 h.</p>
        )}

        <button type="button" className="lancar__24h" onClick={aoVer24h}>Ver as últimas 24 h de {eixo.rotulo}</button>
      </form>
    </div>
  );
}
